import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react'
import clsx from 'clsx'

export type ToastTone = 'success' | 'error' | 'warning' | 'info'

export interface ToastOptions {
  title?: string
  description?: string
  tone?: ToastTone
  /** Milliseconds before auto-dismiss. Default 5000. Pass 0 to keep until dismissed manually. */
  timeoutMs?: number
  /** Optional retry action. When provided, a "Tentar novamente" button is rendered. */
  onRetry?: () => void | Promise<void>
}

interface ToastEntry extends Required<Pick<ToastOptions, 'tone'>> {
  id: string
  title?: string
  description?: string
  timeoutMs: number
  onRetry?: () => void | Promise<void>
}

interface ToastContextValue {
  toast: (opts: ToastOptions) => string
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const toneStyles: Record<ToastTone, { bar: string; Icon: typeof Info; iconClass: string }> = {
  success: { bar: 'bg-success', Icon: CheckCircle2, iconClass: 'text-success' },
  error: { bar: 'bg-danger', Icon: AlertCircle, iconClass: 'text-danger' },
  warning: { bar: 'bg-warning', Icon: AlertTriangle, iconClass: 'text-warning' },
  info: { bar: 'bg-info', Icon: Info, iconClass: 'text-info' },
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<ToastEntry[]>([])
  const timers = useRef<Map<string, number>>(new Map())

  const dismiss = useCallback((id: string) => {
    setEntries((current) => current.filter((entry) => entry.id !== id))
    const handle = timers.current.get(id)
    if (handle) {
      window.clearTimeout(handle)
      timers.current.delete(id)
    }
  }, [])

  const toast = useCallback(
    (opts: ToastOptions) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const entry: ToastEntry = {
        id,
        title: opts.title,
        description: opts.description,
        tone: opts.tone ?? 'info',
        timeoutMs: opts.timeoutMs ?? 5000,
        onRetry: opts.onRetry,
      }
      setEntries((current) => [...current, entry])
      if (entry.timeoutMs > 0) {
        const handle = window.setTimeout(() => dismiss(id), entry.timeoutMs)
        timers.current.set(id, handle)
      }
      return id
    },
    [dismiss],
  )

  useEffect(() => () => {
    timers.current.forEach((handle) => window.clearTimeout(handle))
    timers.current.clear()
  }, [])

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="pointer-events-none fixed bottom-4 right-4 z-[110] flex max-w-sm flex-col gap-2"
      >
        {entries.map((entry) => {
          const { bar, Icon, iconClass } = toneStyles[entry.tone]
          return (
            <div
              key={entry.id}
              role={entry.tone === 'error' || entry.tone === 'warning' ? 'alert' : 'status'}
              className="pointer-events-auto flex w-80 overflow-hidden rounded-lg border border-border-color bg-surface text-foreground shadow-lg"
            >
              <div className={clsx('w-1', bar)} aria-hidden="true" />
              <div className="flex flex-1 items-start gap-3 px-3 py-2.5">
                <Icon className={clsx('mt-0.5 h-4 w-4 flex-shrink-0', iconClass)} aria-hidden="true" />
                <div className="flex-1 text-sm">
                  {entry.title && <p className="font-semibold leading-tight">{entry.title}</p>}
                  {entry.description && (
                    <p className={clsx('text-foreground-secondary', entry.title && 'mt-0.5')}>
                      {entry.description}
                    </p>
                  )}
                  {entry.onRetry && (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await entry.onRetry?.()
                        } finally {
                          dismiss(entry.id)
                        }
                      }}
                      className="mt-1.5 text-xs font-medium text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                    >
                      Tentar novamente
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => dismiss(entry.id)}
                  aria-label="Fechar notificação"
                  className="rounded-md p-1 text-foreground-muted hover:bg-surface-alt hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>')
  return ctx
}
